/**
 * The knobs each ordering takes, in one table.
 *
 * These used to be four hand-written blocks on the Feed shape (Multipliers,
 * Best Of, Wilson, plus the four-field subset the layer row exposed). A layer
 * owns its ordering now, so the layer owns its knobs, and one table is what
 * lets the layer draw the right ones for whatever ordering it is set to.
 *
 * Defaults are the SERVE PATH's defaults, mirrored from the values the Feed
 * blocks showed as placeholders. A blank field means "the default", which is
 * also what an absent key means on the wire — so blanking a field and never
 * having set it are the same feed.
 *
 * `new` and `old` take nothing, which is why two bare `new` layers are refused
 * as duplicates: there is nothing that could tell them apart.
 */

import {
  BlendIcon,
  GaugeIcon,
  HistoryIcon,
  type LucideIcon,
  SparklesIcon,
  TrendingUpIcon,
  TrophyIcon,
} from "lucide-react";

import type { LensOrdering } from "@/lib/lens";

interface OrderingKnob {
  key: string;
  label: string;
  hint?: string;
  default: number;
  min?: number;
  step?: number;
}

const engagement: OrderingKnob[] = [
  { key: "like_count_multiplier", label: "Like", default: 3, min: 0 },
  { key: "reply_count_multiplier", label: "Reply", default: 5, min: 0 },
  { key: "repost_count_multiplier", label: "Repost", default: 3, min: 0 },
  {
    key: "decay_penalty",
    label: "Decay",
    hint: "How much recent activity matters. Low decay strongly favours recent posts; high decay considers older posts more equally.",
    default: 1,
    min: 0,
    step: 0.01,
  },
  {
    key: "time_window",
    label: "Window (h)",
    hint: "How many hours back this layer looks.",
    default: 2,
    min: 0.1,
    step: 0.1,
  },
  {
    key: "reader_like_count_multiplier",
    label: "Reader like",
    hint: "When readers like a post, it is boosted in this layer.",
    default: 3,
    min: 0,
  },
  {
    key: "reader_reply_count_multiplier",
    label: "Reader reply",
    hint: "When readers reply to a post, it is boosted in this layer.",
    default: 5,
    min: 0,
  },
  {
    key: "reader_repost_count_multiplier",
    label: "Reader repost",
    hint: "When readers repost a post, it is boosted in this layer.",
    default: 3,
    min: 0,
  },
  {
    key: "request_less_count_multiplier",
    label: "Request less",
    hint: "Readers can click 'Request less' on posts in your feed; this weighs that.",
    default: 1,
  },
  {
    key: "request_more_count_multiplier",
    label: "Request more",
    hint: "Readers can click 'Request more' on posts in your feed; this weighs that.",
    default: 1,
  },
];

const bestOf: OrderingKnob[] = [
  {
    key: "gravity",
    label: "Gravity",
    hint: "Higher gravity: old posts lose rank faster. Lower: they stay relevant longer.",
    default: 1.8,
    min: 0.1,
    step: 0.1,
  },
  {
    key: "time_offset",
    label: "Time offset",
    hint: "Shifts the point from which a post's rank begins to decay, giving it more or less time at the top.",
    default: 2,
    min: 0.1,
    step: 0.1,
  },
];

const wilson: OrderingKnob[] = [
  {
    key: "wilson_confidence_z",
    label: "Confidence z",
    hint: "Z-score for the Wilson lower bound; 1.96 is 95% confidence.",
    default: 1.96,
    min: 0.1,
    step: 0.01,
  },
  {
    key: "time_window",
    label: "Window (h)",
    hint: "How many hours back this layer looks.",
    default: 48,
    min: 0.1,
    step: 0.1,
  },
  { key: "wilson_interaction_like_multiplier", label: "Like", default: 1.0, min: 0, step: 0.1 },
  { key: "wilson_interaction_reply_multiplier", label: "Reply", default: 2.0, min: 0, step: 0.1 },
  { key: "wilson_interaction_repost_multiplier", label: "Repost", default: 1.5, min: 0, step: 0.1 },
  { key: "wilson_interaction_quote_multiplier", label: "Quote", default: 1.5, min: 0, step: 0.1 },
  {
    key: "wilson_request_more_multiplier",
    label: "Request more",
    hint: "Weight of 'request more' (a positive signal).",
    default: 2.0,
    step: 0.1,
  },
  {
    key: "wilson_request_less_multiplier",
    label: "Request less",
    hint: "Weight of 'request less' (a negative signal).",
    default: -2.0,
    step: 0.1,
  },
];

export const orderingKnobs: Record<LensOrdering, OrderingKnob[]> = {
  new: [],
  old: [],
  trending: engagement,
  blend: engagement,
  hackernews: bestOf,
  wilson,
};

/** Every knob key any ordering takes: the keys that move with a layer. */
export const ORDERING_SETTING_KEYS: ReadonlySet<string> = new Set(
  Object.values(orderingKnobs).flatMap((knobs) => knobs.map((k) => k.key)),
);

/**
 * One glyph per ordering, so the Page palette and each layer's header say the
 * same thing at a glance: what is new, what is climbing, what is a mix, what
 * is old, what has proven itself, what is statistically confident.
 */
export const orderingIcons: Record<LensOrdering, LucideIcon> = {
  new: SparklesIcon,
  trending: TrendingUpIcon,
  blend: BlendIcon,
  old: HistoryIcon,
  hackernews: TrophyIcon,
  wilson: GaugeIcon,
};
